import React, { Suspense, lazy, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { AlertCircle, CheckCircle2, Loader2, WifiOff, XCircle, X } from 'lucide-react';
import { auth, db } from './services/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  updateDoc,
  getDoc,
  setDoc,
  deleteField,
  type DocumentReference
} from 'firebase/firestore';

import ChatHeader from './components/ChatHeader';
import ChatDateSeparator from './components/ChatDateSeparator';
import EmptyChatActions from './components/EmptyChatActions';
import MessageBubble from './components/MessageBubble';
import MessageInput from './components/MessageInput';
import Login from './components/Login';
import Onboarding from './components/Onboarding';
import Sidebar from './components/Sidebar';
import { ReportModal } from './components/ReportModal';
import TypingIndicator from './components/TypingIndicator';
import { usePanelNavigation } from './hooks/usePanelNavigation';
import { UsagePreviewContext, useAdminUsagePreview } from './features/usagePreview/context';
import { UsagePreviewChat } from './features/usagePreview/UsagePreviewChat';
import { previewSendingDisabled } from './features/usagePreview/model';
import { EconomicBillingContext, useEconomicBillingController } from './features/economicBilling/context';
import { economicSendingDisabled, isEconomicAdmissionError } from './features/economicBilling/model';
import { EconomicBillingChat } from './features/economicBilling/EconomicBillingChat';
import { useEconomicBillingCompletionRefresh } from './features/economicBilling/useCompletionRefresh';
import DiligenceTemplateLibrary from './components/DiligenceTemplateLibrary';
import { isSubscriptionTier, SubscriptionTier } from './config/subscriptionPlans';
import { ChatMessagePayload, Message, type ReasoningPreference } from './types';
import type { ChatMention, MentionableChat } from './shared/chatMentions';
import { canUseReasoningPreference, getAuthorizedReasoningPreference, normalizeReasoningPreference } from './shared/reasoningPreference';
import { cancelMessageGeneration, deleteUploadedChatImages, getSendMessageNetworkErrorKind, isRecoverableSendMessageError, sendChatMessage, uploadChatImages, uploadFileToFirebase } from './services/api';
import { checkUsageLimit, ensureUserDoc, recordCompletedMessageUsage } from './services/userService';
import { logTechnicalError } from './services/analytics';
import { useModalAccessibility } from './hooks/useModalAccessibility';
import { CHAT_COLLECTION, SERVICE_STATUS_API_URL } from './config/runtime';
import { downloadChatDocument, registerDiligenceAttachment } from './services/diligenceAttachmentService';
import { isDiligenceUploadFailure } from './services/diligenceUploadFailure';
import { downloadDiligenceArtifact } from './services/diligenceArtifactService';
import { createChatArtifactTemplateResolver } from './services/artifactTemplateMetadataService';
import { downloadChatImage } from './services/chatImageService';
import { createChat } from './services/chatCreationService';
import {
  countLocalMessagesForChat,
  getRetryImageFields,
  isNewChatSurface,
  isTypingIndicatorScopedToChat,
  mergeOptimisticAttachmentPreview,
  shouldShowTypingIndicator,
  shouldRenderPendingMessage,
  upsertOptimisticAttachmentPreview
} from './utils/chatUiState';
import { getActiveDocumentGeneration } from './utils/documentUiState';
import { getActivityStartTime } from './utils/documentActivityTiming';
import { canAutoFocusComposer } from './utils/composerFocus';
import { createComposerDraftStore } from './utils/composerDrafts';

// CONSTANTES PARA LOCAL STORAGE
const LOCAL_STORAGE_KEY = 'juris_pending_typing';
const RESPONSE_RECOVERY_ONLINE_TIMEOUT_MS = 12_000;
const RESPONSE_RECOVERY_OFFLINE_TIMEOUT_MS = 90_000;
const ASYNC_CALLBACK_RESPONSE_TIMEOUT_MS = 270_000;
const LOCAL_OFFLINE_MESSAGE_PREFIX = 'local_offline_';
const SERVICE_STATUS_POLL_MS = 60_000;
const MAINTENANCE_MESSAGE = 'Juris se encuentra en mantenimiento. Disculpa las molestias; volverá a estar disponible lo antes posible.';

type ServiceAvailabilityStatus = 'online' | 'maintenance';

const MAINTENANCE_ERROR_KINDS = new Set([
  'upstream_http_error',
  'upstream_timeout',
  'upstream_fetch_error'
]);

interface ActiveGeneration {
  chatId: string;
  userMessageId: string;
  botMessageId: string;
}

interface TransientChatError {
  chatId: string;
  botMessageId: string;
  message: Message;
}

interface LocalOptimisticAttachment {
  imageUrl?: string;
  imageUrls?: string[];
  audioUrl?: string;
}

interface RetryMessagePayload {
  text?: string;
  chatMentions?: ChatMention[];
  images: File[];
  audio: Blob | null;
  document: File | null;
  attachmentIds?: string[];
}

interface LocalOfflineMessage extends Message {
  localChatId: string | null;
}

interface PendingOptimisticMessage extends Message {
  localChatId: string | null;
  persistedMessageId?: string;
}

type PersistedBotWaitResult = 'found' | 'canceled' | 'failed' | 'timeout';
type MessagesLoadState = 'idle' | 'loading' | 'ready' | 'error';

const focusChatComposer = () => {
  if (!canAutoFocusComposer()) return false;
  const input = document.getElementById('chat-input');
  if (!(input instanceof HTMLTextAreaElement)) return false;
  input.focus({ preventScroll: true });
  return document.activeElement === input;
};

const getLocalDateKey = (isoString?: string) => {
  if (!isoString) return null;

  const date = new Date(isoString);
  if (!Number.isFinite(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const UserProfileModal = lazy(() =>
  import('./components/UserProfileModal').then((module) => ({ default: module.UserProfileModal }))
);
const PlansModal = lazy(() => import('./components/PlansModal'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const EconomicLabPanel = lazy(() => import('./components/EconomicLabPanel'));

const AppLoadingScreen: React.FC = () => (
  <div className="min-h-[100dvh] w-full flex items-center justify-center bg-canvas px-6 font-sans text-ink">
    <div className="flex flex-col items-center text-center motion-fade-through">
      <div className="relative mb-5">
        <div className="absolute inset-0 rounded-full bg-juris-soft/30 animate-ping"></div>
        <div className="relative w-24 h-24 md:w-28 md:h-28">
          <img
            src="/juris-logo.png"
            alt="Juris"
            width={112}
            height={112}
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
      </div>
      <p className="text-base md:text-lg font-bold tracking-tight">Cargando Juris</p>
      <div className="mt-4 flex items-center gap-1.5" aria-hidden="true">
        <span className="h-2 w-2 rounded-full bg-action/70 animate-bounce [animation-delay:-0.2s]"></span>
        <span className="h-2 w-2 rounded-full bg-action/70 animate-bounce [animation-delay:-0.1s]"></span>
        <span className="h-2 w-2 rounded-full bg-action/70 animate-bounce"></span>
      </div>
    </div>
  </div>
);

const ChatStatusNotice: React.FC<{
  tone: 'loading' | 'offline' | 'error';
  title: string;
  detail: string;
}> = ({ tone, title, detail }) => {
  const Icon = tone === 'loading' ? Loader2 : tone === 'offline' ? WifiOff : AlertCircle;
  const toneClass = tone === 'error'
    ? 'border-danger bg-danger-soft/90 text-danger'
    : 'border-line bg-panel/80 text-ink';

  return (
    <div className="w-full flex justify-center px-2 py-3" role="status" aria-live="polite">
      <div className={`inline-flex max-w-lg items-center gap-3 rounded-xl border px-3 py-2 text-sm shadow-sm backdrop-blur ${toneClass}`}>
        <Icon size={18} className={tone === 'loading' ? 'animate-spin flex-shrink-0' : 'flex-shrink-0'} />
        <div className="leading-snug">
          <p className="font-bold">{title}</p>
          <p className="text-xs opacity-80">{detail}</p>
        </div>
      </div>
    </div>
  );
};

const revokeObjectUrl = (url: string | null) => {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

const scheduleObjectUrlRevoke = (url: string | null, delayMs = 60_000) => {
  if (!url?.startsWith('blob:')) return;

  window.setTimeout(() => {
    revokeObjectUrl(url);
  }, delayMs);
};

const getPersistedBotMessageId = (clientMessageId: string) => `bot_${clientMessageId}`;

const createLocalOfflineMessageId = () => `${LOCAL_OFFLINE_MESSAGE_PREFIX}${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const getPersistedAttachmentUrl = (url?: string) => {
  if (!url || url.startsWith('blob:')) return undefined;
  return url;
};
const getPersistedImageUrls = (message: Pick<Message, 'imageUrl' | 'imageUrls'>) => (
  (message.imageUrls?.length ? message.imageUrls : message.imageUrl ? [message.imageUrl] : [])
    .map((url) => getPersistedAttachmentUrl(url))
    .filter((url): url is string => Boolean(url))
);
const scheduleImageUrlsRevoke = (urls?: string[], delayMs?: number) => {
  urls?.forEach((url) => scheduleObjectUrlRevoke(url, delayMs));
};

const isAdminPath = (pathname: string) => pathname === '/admin' || pathname.startsWith('/admin/');

const isSupportedChatImageFile = (file: File) => (
  ['image/png', 'image/jpeg', 'image/webp'].includes(file.type) && file.size > 0 && file.size < 20 * 1024 * 1024
);
const areSupportedChatImages = (images: File[]) => (
  images.length <= 5 && images.every(isSupportedChatImageFile)
);

const isSupportedChatAudioBlob = (blob: Blob | null) => !blob || !blob.type || blob.type.startsWith('audio/');

const getApiErrorKind = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  return typeof (error as { errorKind?: unknown }).errorKind === 'string'
    ? (error as { errorKind: string }).errorKind
    : undefined;
};

const isMaintenanceErrorKind = (errorKind?: string) => (
  Boolean(errorKind && MAINTENANCE_ERROR_KINDS.has(errorKind))
);

const waitForPersistedBotMessage = (
  chatId: string,
  userMessageId: string,
  botMessageId: string,
  timeoutMsOverride?: number
): Promise<PersistedBotWaitResult> => {
  return new Promise((resolve) => {
    const userMessageRef = doc(db, CHAT_COLLECTION, chatId, 'messages', userMessageId);
    const botMessageRef = doc(db, CHAT_COLLECTION, chatId, 'messages', botMessageId);
    const timeoutMs = timeoutMsOverride ?? (
      navigator.onLine === false
        ? RESPONSE_RECOVERY_OFFLINE_TIMEOUT_MS
        : RESPONSE_RECOVERY_ONLINE_TIMEOUT_MS
    );

    let settled = false;
    const cleanup: Array<() => void> = [];

    const finish = (result: PersistedBotWaitResult) => {
      if (settled) return;
      settled = true;
      cleanup.forEach((cleanupFn) => cleanupFn());
      resolve(result);
    };

    const timeoutId = window.setTimeout(() => finish('timeout'), timeoutMs);
    cleanup.push(() => window.clearTimeout(timeoutId));

    const unsubscribe = onSnapshot(botMessageRef, (snapshot) => {
      if (snapshot.exists()) {
        finish('found');
      }
    }, (error) => {
      console.warn('No se pudo escuchar la respuesta persistida del proxy.', error);
      if (error?.code === 'permission-denied') {
        finish('timeout');
      }
    });
    cleanup.push(unsubscribe);

    const unsubscribeUser = onSnapshot(userMessageRef, (snapshot) => {
      if (snapshot.data()?.generationStatus === 'canceled') {
        finish('canceled');
      } else if (snapshot.data()?.generationStatus === 'failed') {
        finish('failed');
      }
    }, (error) => {
      console.warn('No se pudo escuchar el estado de cancelación del mensaje.', error);
    });
    cleanup.push(unsubscribeUser);

    getDoc(botMessageRef)
      .then((snapshot) => {
        if (snapshot.exists()) {
          finish('found');
        }
      })
      .catch((error) => {
        console.warn('No se pudo comprobar la respuesta persistida del proxy.', error);
      });
  });
};

const App: React.FC = () => {
  // --- LÓGICA DE ESTADO ---
  const [user, setUser] = useState<User | null>(null);
  const [currentPath, setCurrentPath] = useState(() => window.location.pathname);

  const [authLoading, setAuthLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileCompleted, setProfileCompleted] = useState(false);

  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [composerDrafts] = useState(createComposerDraftStore);
  const [composerDraftRevision, setComposerDraftRevision] = useState(0);
  const [newDraftId, setNewDraftId] = useState(0);
  const composerDraftBinding = useMemo(() => user
    ? composerDrafts.bind(user.uid, currentChatId, newDraftId) : undefined,
  [composerDrafts, currentChatId, newDraftId, user]);
  const [mentionableChats, setMentionableChats] = useState<MentionableChat[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesChatId, setMessagesChatId] = useState<string | null>(null);
  const [localOfflineMessages, setLocalOfflineMessages] = useState<LocalOfflineMessage[]>([]);
  const [pendingOptimisticMessage, setPendingOptimisticMessage] = useState<PendingOptimisticMessage | null>(null);
  const [messagesLoadState, setMessagesLoadState] = useState<MessagesLoadState>('idle');
  const [isOnline, setIsOnline] = useState(() => navigator.onLine !== false);
  const [localOptimisticAttachments, setLocalOptimisticAttachments] = useState<Record<string, LocalOptimisticAttachment>>({});

  // ESTADO VINCULADO AL ID DEL CHAT
  const [typingChatId, setTypingChatId] = useState<string | null>(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ESTADO PARA EL MODAL DE REPORTES
  const [isReportModalOpen, setReportModalOpen] = useState(false);

  // ESTADO PARA FEEDBACK POST-CHECKOUT
  const [checkoutResult, setCheckoutResult] = useState<'success' | 'canceled' | null>(null);

  // ESTADO PARA EL MODAL DE PERFIL/SUSCRIPCIÓN
  const { panel, openPanel, closePanel, navigateAfterPanel } = usePanelNavigation(Boolean(user && profileCompleted) && !isAdminPath(currentPath));
  const showProfileModal = panel === 'settings';
  const showTemplateLibrary = panel === 'templates';
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [userTier, setUserTier] = useState<SubscriptionTier>('free');
  const usagePreview = useAdminUsagePreview(user?.uid ?? null, userTier);
  const economicBilling = useEconomicBillingController(user?.uid ?? null);
  const isPreviewSendingDisabled = previewSendingDisabled(usagePreview?.state) || economicSendingDisabled(economicBilling.status, Boolean(usagePreview?.state), economicBilling.recoveryRequired);
  const acknowledgePreviewMessage = () => {
    const state = usagePreview?.state;
    if (state) usagePreview.dispatch({ type: 'messageSent', cycle: state.cycle, activation: state.creditActivation });
  };
  const [reasoningPreference, setReasoningPreference] = useState<ReasoningPreference>('normal');
  const [isSavingReasoning, setIsSavingReasoning] = useState(false);
  const [reasoningError, setReasoningError] = useState<string | null>(null);

  // Estado de respuesta pendiente y animacion visual de Juris.
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [typingStartTime, setTypingStartTime] = useState<number | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const checkoutDialogRef = useRef<HTMLDivElement>(null);
  const lastChatIdRef = useRef<string | null>(null);
  const pendingInitialScrollChatIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sendCancelledRef = useRef(false);
  const activeGenerationRef = useRef<ActiveGeneration | null>(null);
  const pendingOptimisticMessageRef = useRef<PendingOptimisticMessage | null>(null);
  const currentChatIdRef = useRef<string | null>(null);
  const shouldAutoFollowMessagesRef = useRef(true);

  const commitPendingOptimisticMessage = useCallback((message: PendingOptimisticMessage | null) => {
    pendingOptimisticMessageRef.current = message;
    setPendingOptimisticMessage(message);
  }, []);
  const userPausedAutoFollowRef = useRef(false);
  const autoScrollResetTimeoutRef = useRef<number | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const pendingBotAnimationIdsRef = useRef<Set<string>>(new Set());
  const completedBotAnimationIdsRef = useRef<Set<string>>(new Set());
  const expectedBotMessageChatIdsRef = useRef<Map<string, string>>(new Map());
  const leftChatBeforeBotResponseIdsRef = useRef<Set<string>>(new Set());
  const returnedUnreadBotMessageIdsRef = useRef<Set<string>>(new Set());
  const messageNodeRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const retryPayloadsRef = useRef<Record<string, RetryMessagePayload>>({});
  const canceledClientMessageIdsRef = useRef<Set<string>>(new Set());
  const localOptimisticAttachmentsRef = useRef<Record<string, LocalOptimisticAttachment>>({});
  const [locallyCanceledMessageIds, setLocallyCanceledMessageIds] = useState<Set<string>>(() => new Set());
  const [retryingMessageIds, setRetryingMessageIds] = useState<Set<string>>(() => new Set());
  const [animatingBotMessageId, setAnimatingBotMessageId] = useState<string | null>(null);
  const [transientChatError, setTransientChatError] = useState<TransientChatError | null>(null);
  const [preparingSendChatIds, setPreparingSendChatIds] = useState<Set<string>>(() => new Set());
  const [serviceStatus, setServiceStatus] = useState<ServiceAvailabilityStatus>('online');
  const serviceStatusRef = useRef<ServiceAvailabilityStatus>('online');

  const updateServiceStatus = useCallback((nextStatus: ServiceAvailabilityStatus) => {
    serviceStatusRef.current = nextStatus;
    setServiceStatus(nextStatus);
  }, []);

  const revokeLocalOptimisticAttachments = useCallback((attachments: Record<string, LocalOptimisticAttachment>) => {
    Object.values(attachments).forEach((attachment) => {
      revokeObjectUrl(attachment.imageUrl || null);
      attachment.imageUrls?.forEach((url) => revokeObjectUrl(url));
      revokeObjectUrl(attachment.audioUrl || null);
    });
  }, []);

  const clearLocalOptimisticAttachments = useCallback(() => {
    setLocalOptimisticAttachments((current) => {
      revokeLocalOptimisticAttachments(current);
      localOptimisticAttachmentsRef.current = {};
      return {};
    });
  }, [revokeLocalOptimisticAttachments]);

  const preserveLocalOptimisticAttachment = useCallback((messageId: string, attachment: LocalOptimisticAttachment) => {
    setLocalOptimisticAttachments((previous) => {
      const next = upsertOptimisticAttachmentPreview(previous, messageId, attachment);
      localOptimisticAttachmentsRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const setAppHeight = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty('--juris-app-height', `${viewportHeight}px`);
    };

    setAppHeight();
    const delayedUpdateId = window.setTimeout(setAppHeight, 250);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setAppHeight();
        window.setTimeout(setAppHeight, 250);
      }
    };

    window.addEventListener('resize', setAppHeight);
    window.addEventListener('orientationchange', setAppHeight);
    window.addEventListener('focus', setAppHeight);
    window.visualViewport?.addEventListener('resize', setAppHeight);
    window.visualViewport?.addEventListener('scroll', setAppHeight);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearTimeout(delayedUpdateId);
      window.removeEventListener('resize', setAppHeight);
      window.removeEventListener('orientationchange', setAppHeight);
      window.removeEventListener('focus', setAppHeight);
      window.visualViewport?.removeEventListener('resize', setAppHeight);
      window.visualViewport?.removeEventListener('scroll', setAppHeight);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const navigateToPath = useCallback((nextPath: string) => {
    navigateAfterPanel(() => {
      if (window.location.pathname !== nextPath) window.history.pushState({}, '', nextPath);
      setCurrentPath(window.location.pathname);
    });
  }, [navigateAfterPanel]);

  const handleOpenAdminDashboard = useCallback(() => {
    setSidebarOpen(false);
    navigateToPath('/admin');
  }, [navigateToPath]);

  const handleBackToChat = useCallback(() => {
    navigateToPath('/');
  }, [navigateToPath]);

  const refreshServiceStatus = useCallback(async () => {
    try {
      const response = await fetch(SERVICE_STATUS_API_URL, {
        method: 'GET',
        cache: 'no-store'
      });

      if (!response.ok) return;

      const data = await response.json().catch((): unknown => null);
      const nextStatus = typeof data === 'object' && data !== null && 'status' in data
        ? (data as { status?: unknown }).status
        : undefined;

      if (nextStatus === 'online' || nextStatus === 'maintenance') {
        updateServiceStatus(nextStatus);
      }
    } catch {
      // Client network failures should not flip Juris into maintenance mode.
    }
  }, [updateServiceStatus]);

  const markChatSendPreparing = useCallback((chatId: string) => {
    setPreparingSendChatIds((current) => {
      if (current.has(chatId)) return current;
      const next = new Set(current);
      next.add(chatId);
      return next;
    });
  }, []);

  const clearChatSendPreparing = useCallback((chatId: string | null) => {
    if (!chatId) return;
    setPreparingSendChatIds((current) => {
      if (!current.has(chatId)) return current;
      const next = new Set(current);
      next.delete(chatId);
      return next;
    });
  }, []);

  const scrollMessagesToBottom = useCallback((behavior: ScrollBehavior = 'smooth', force = false) => {
    if (!force && !shouldAutoFollowMessagesRef.current) return;

    const scrollContainer = messagesScrollRef.current;
    isProgrammaticScrollRef.current = true;

    if (autoScrollResetTimeoutRef.current) {
      window.clearTimeout(autoScrollResetTimeoutRef.current);
    }
    autoScrollResetTimeoutRef.current = window.setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, behavior === 'smooth' ? 320 : 80);

    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior
      });
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  }, []);

  const scrollMessageToTop = useCallback((messageId: string) => {
    const targetNode = messageNodeRefs.current.get(messageId);
    if (!targetNode) return false;

    isProgrammaticScrollRef.current = true;

    if (autoScrollResetTimeoutRef.current) {
      window.clearTimeout(autoScrollResetTimeoutRef.current);
    }
    autoScrollResetTimeoutRef.current = window.setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 120);

    targetNode.scrollIntoView({ behavior: 'auto', block: 'start' });
    return true;
  }, []);

  const setMessageNodeRef = useCallback((messageId: string, node: HTMLDivElement | null) => {
    if (node) {
      messageNodeRefs.current.set(messageId, node);
    } else {
      messageNodeRefs.current.delete(messageId);
    }
  }, []);

  const handleMessagesScroll = useCallback(() => {
    const scrollContainer = messagesScrollRef.current;
    if (!scrollContainer || isProgrammaticScrollRef.current) return;

    const distanceFromBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
    const isNearBottom = distanceFromBottom < 96;
    const isAtBottom = distanceFromBottom < 8;

    if (userPausedAutoFollowRef.current) {
      if (isAtBottom) {
        userPausedAutoFollowRef.current = false;
        shouldAutoFollowMessagesRef.current = true;
      } else {
        shouldAutoFollowMessagesRef.current = false;
      }
      return;
    }

    shouldAutoFollowMessagesRef.current = isNearBottom;
  }, []);

  const handleManualScrollIntent = useCallback((event?: React.SyntheticEvent) => {
    const scrollContainer = messagesScrollRef.current;
    if (scrollContainer && isProgrammaticScrollRef.current && event?.type !== 'touchmove') {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollTop,
        behavior: 'auto'
      });
    }
    isProgrammaticScrollRef.current = false;
    userPausedAutoFollowRef.current = true;
    shouldAutoFollowMessagesRef.current = false;
  }, []);

  const followStreamingMessage = useCallback(() => {
    scrollMessagesToBottom('auto');
  }, [scrollMessagesToBottom]);

  useModalAccessibility(checkoutDialogRef, {
    enabled: !!checkoutResult,
    onClose: () => setCheckoutResult(null)
  });

  // 0. Detectar retorno de Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success' || checkout === 'canceled') {
      setCheckoutResult(checkout);
      // Limpia la URL sin recargar la página
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    currentChatIdRef.current = currentChatId;
    pendingInitialScrollChatIdRef.current = currentChatId;
  }, [currentChatId]);

  useEffect(() => {
    serviceStatusRef.current = serviceStatus;
  }, [serviceStatus]);

  useEffect(() => {
    refreshServiceStatus();

    const intervalId = window.setInterval(refreshServiceStatus, SERVICE_STATUS_POLL_MS);
    const handleFocus = () => refreshServiceStatus();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshServiceStatus();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshServiceStatus]);

  useEffect(() => {
    localOptimisticAttachmentsRef.current = localOptimisticAttachments;
  }, [localOptimisticAttachments]);

  useEffect(() => {
    return () => {
      if (autoScrollResetTimeoutRef.current) {
        window.clearTimeout(autoScrollResetTimeoutRef.current);
      }
      revokeLocalOptimisticAttachments(localOptimisticAttachmentsRef.current);
    };
  }, [revokeLocalOptimisticAttachments]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 1. Auth Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      composerDrafts.setUser(currentUser?.uid ?? null);
      if (currentUser) {
        setProfileLoading(true);
        setUser(currentUser);
      } else {
        setUser(null);
        setAuthLoading(false);
        setProfileLoading(false);
      }
    });
    return () => { unsubscribe(); composerDrafts.setUser(null); };
  }, [composerDrafts]);

  useEffect(() => { setMentionableChats([]); }, [user?.uid]);

  // --- LIMPIEZA AL LOGOUT ---
  useEffect(() => {
    if (!user) {
      setCurrentChatId(null);
      setMessages([]);
      setLocalOfflineMessages([]);
      setPendingOptimisticMessage(null);
      pendingOptimisticMessageRef.current = null;
      setMessagesLoadState('idle');
      setTypingChatId(null);
      setSidebarOpen(false);
      if (showProfileModal || showTemplateLibrary) closePanel();
      setShowPlansModal(false);
      setReasoningPreference('normal');
      setCheckoutResult(null);
      setProfileCompleted(false);
      setReportModalOpen(false);
      setLocallyCanceledMessageIds(new Set());
      setRetryingMessageIds(new Set());
      setAnimatingBotMessageId(null);
      setStreamingMessageId(null);
      clearLocalOptimisticAttachments();
      setPreparingSendChatIds(new Set());
      pendingBotAnimationIdsRef.current = new Set();
      completedBotAnimationIdsRef.current = new Set();
      expectedBotMessageChatIdsRef.current = new Map();
      leftChatBeforeBotResponseIdsRef.current = new Set();
      returnedUnreadBotMessageIdsRef.current = new Set();
      messageNodeRefs.current = new Map();
      retryPayloadsRef.current = {};
      canceledClientMessageIdsRef.current = new Set();
      setTransientChatError(null);
      activeGenerationRef.current = null;
    }
  }, [clearLocalOptimisticAttachments, closePanel, showProfileModal, showTemplateLibrary, user]);

  // ===========================================================================
  // GATEKEEPER
  // ===========================================================================
  useEffect(() => {
    if (!user) return;
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    const setup = async () => {
      setProfileLoading(true);
      try {
        await ensureUserDoc(user.uid, user.email);
        if (cancelled) return;

        const userDocRef = doc(db, 'users', user.uid);

        unsubscribe = onSnapshot(userDocRef, (docSnap) => {
          if (localStorage.getItem('juris_account_deleted') === 'true') {
            setProfileLoading(false);
            return;
          }

          const userData = docSnap.data();
          const hasStats = userData?.profileStats && userData.profileStats.cuerpo;

          if (hasStats) {
            setProfileCompleted(true);
          } else {
            setProfileCompleted(false);
          }

          if (isSubscriptionTier(userData?.tier)) {
            setUserTier(userData.tier);
          } else {
            setUserTier('free');
          }

          setProfileLoading(false);
          setAuthLoading(false);
        }, (error) => {
          if (error?.code === 'permission-denied' && localStorage.getItem('juris_account_deleted') === 'true') return;
          console.error("Error en listener de usuario:", error);
          logTechnicalError(error, "App:onSnapshot:userDoc");
        });
      } catch (error) {
        if (cancelled) return;
        console.error("Error preparando perfil de usuario:", error);
        logTechnicalError(error, "App:ensureUserDoc");
        setProfileLoading(false);
        setAuthLoading(false);
      }
    };

    setup();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [user]);


  // 2. Load Messages
  useEffect(() => {
    if (!currentChatId || !user || !profileCompleted) {
      setMessages([]);
      setMessagesChatId(null);
      setMessagesLoadState('idle');
      return;
    }

    setMessages([]);
    setMessagesChatId(null);
    setMessagesLoadState('loading');

    const q = query(
      collection(db, CHAT_COLLECTION, currentChatId, 'messages'),
      orderBy('timestamp', 'asc')
    );

    const resolveTemplateNames = createChatArtifactTemplateResolver(user.uid, currentChatId);
    let listenerActive = true;
    let snapshotRevision = 0;
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const revision = ++snapshotRevision;
      const msgs = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : new Date().toISOString()
        };
      }) as Message[];
      setMessages(msgs);
      setMessagesChatId(currentChatId);
      setMessagesLoadState('ready');
      void resolveTemplateNames(msgs).then(enriched => {
        if (listenerActive && revision === snapshotRevision) setMessages(enriched);
      }).catch(() => {});
    }, (error) => {
      if (error?.code === 'permission-denied' && localStorage.getItem('juris_account_deleted') === 'true') return;
      console.error("Error en listener de mensajes:", error);
      logTechnicalError(error, "App:onSnapshot:messages");
      setMessagesChatId(null);
      setMessagesLoadState('error');
    });

    return () => { listenerActive = false; unsubscribe(); };
  }, [currentChatId, user, profileCompleted]);

  useEffect(() => {
    if (!user || !profileCompleted || currentChatIdRef.current) return;

    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!saved) return;

    try {
      const { chatId, startTime, userMessageId, botMessageId } = JSON.parse(saved);
      const ageInSeconds = (Date.now() - Number(startTime)) / 1000;

      if (typeof chatId !== 'string' || !chatId || !Number.isFinite(ageInSeconds) || ageInSeconds >= 180) {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        return;
      }

      setCurrentChatId(chatId);
      currentChatIdRef.current = chatId;
      pendingInitialScrollChatIdRef.current = chatId;
      setTypingChatId(chatId);
      setTypingStartTime(Number(startTime));

      if (typeof botMessageId === 'string' && botMessageId) {
        expectedBotMessageChatIdsRef.current.set(botMessageId, chatId);
        pendingBotAnimationIdsRef.current.add(botMessageId);
        setStreamingMessageId(botMessageId);
      }

      if (typeof userMessageId === 'string' && typeof botMessageId === 'string') {
        activeGenerationRef.current = {
          chatId,
          userMessageId,
          botMessageId
        };
      }
    } catch (error) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  }, [profileCompleted, user]);

  // 3. PERSISTENCIA DEL ESTADO DE "ESCRIBIENDO" (SURVIVE REFRESH)
  useEffect(() => {
    if (!currentChatId || !user || !messages.length) return;

    const lastMessage = messages[messages.length - 1];

    // Si el último mensaje es del bot, limpiamos cualquier estado persistente de "escribiendo"
    if (lastMessage.sender === 'bot') {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      return;
    }

    // Intentamos recuperar si había una consulta en curso
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        const { chatId, startTime, userMessageId, botMessageId } = JSON.parse(saved);
        const ageInSeconds = (Date.now() - startTime) / 1000;

        // Solo restauramos si coincide el chat y tiene menos de 3 minutos
        if (chatId === currentChatId && ageInSeconds < 180) {
          setTypingChatId(chatId);
          setTypingStartTime(startTime);
          if (typeof botMessageId === 'string') {
            expectedBotMessageChatIdsRef.current.set(botMessageId, chatId);
            pendingBotAnimationIdsRef.current.add(botMessageId);
            setStreamingMessageId(botMessageId);
          }
          if (typeof userMessageId === 'string' && typeof botMessageId === 'string') {
            activeGenerationRef.current = {
              chatId,
              userMessageId,
              botMessageId
            };
          }
        }
      } catch (e) {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }
  }, [currentChatId, messages, user]);

  const canceledClientMessageIds = useMemo(() => {
    const canceled = new Set(locallyCanceledMessageIds);
    messages.forEach((message) => {
      if (message.sender === 'user' && message.generationStatus === 'canceled') {
        canceled.add(message.id);
      }
    });
    return canceled;
  }, [locallyCanceledMessageIds, messages]);

  useEffect(() => {
    canceledClientMessageIdsRef.current = canceledClientMessageIds;
  }, [canceledClientMessageIds]);

  const visibleMessages = useMemo(() => {
    const firestoreMessages = messages
      .filter((message) => {
        if (message.sender !== 'bot' || !message.sourceClientMessageId) return true;
        return !canceledClientMessageIds.has(message.sourceClientMessageId);
      })
      .map((message) => {
        const localAttachment = localOptimisticAttachments[message.id];
        return mergeOptimisticAttachmentPreview(message, localAttachment);
      });

    const currentLocalMessages = localOfflineMessages.filter((message) => message.localChatId === currentChatId);
    const shouldShowPendingMessage = shouldRenderPendingMessage(
      pendingOptimisticMessage,
      currentChatId,
      new Set(firestoreMessages.map((message) => message.id))
    );

    return [
      ...firestoreMessages,
      ...currentLocalMessages,
      ...(shouldShowPendingMessage ? [pendingOptimisticMessage] : [])
    ];
  }, [canceledClientMessageIds, currentChatId, localOfflineMessages, localOptimisticAttachments, messages, pendingOptimisticMessage]);

  const userMessageStartTimes = useMemo(() => new Map(
    visibleMessages
      .filter((message) => message.sender === 'user')
      .map((message) => [message.id, getActivityStartTime(message.documentRun, Date.parse(message.timestamp))])
  ), [visibleMessages]);
  const userTurnRuns = useMemo(() => new Map(visibleMessages
    .filter((message) => message.sender === 'user')
    .map((message) => [message.id, message.documentRun])), [visibleMessages]);

  const activeDocumentGeneration = useMemo(
    () => getActiveDocumentGeneration(visibleMessages),
    [visibleMessages]
  );
  const isInputLoading = Boolean(pendingOptimisticMessage)
    || (!!typingChatId && typingChatId === currentChatId)
    || Boolean(activeDocumentGeneration);
  useEconomicBillingCompletionRefresh({
    uid: user?.uid ?? null,
    chatId: currentChatId,
    enabled: Boolean(economicBilling.status && economicBilling.status.tier !== 'admin'),
    loading: isInputLoading,
    ready: messagesLoadState === 'ready' && messagesChatId === currentChatId,
    lastBotId: visibleMessages.filter(message => message.sender === 'bot').at(-1)?.id ?? null
  }, economicBilling.refresh);

  useEffect(() => {
    const persistedMessageId = pendingOptimisticMessage?.persistedMessageId;
    if (!persistedMessageId || !messages.some((message) => message.id === persistedMessageId)) return;

    setPendingOptimisticMessage(null);
    pendingOptimisticMessageRef.current = null;
  }, [messages, pendingOptimisticMessage]);

  const markPendingResponseLeftChat = useCallback((chatId: string | null) => {
    if (!chatId) return;

    const visibleBotMessageIds = new Set(
      visibleMessages
        .filter((message) => message.sender === 'bot')
        .map((message) => message.id)
    );

    expectedBotMessageChatIdsRef.current.forEach((expectedChatId, botMessageId) => {
      if (
        expectedChatId === chatId
        && pendingBotAnimationIdsRef.current.has(botMessageId)
        && !completedBotAnimationIdsRef.current.has(botMessageId)
        && !visibleBotMessageIds.has(botMessageId)
      ) {
        leftChatBeforeBotResponseIdsRef.current.add(botMessageId);
      }
    });
  }, [visibleMessages]);

  const hasStreamingBotMessage = useMemo(() => {
    return Boolean(
      streamingMessageId &&
      visibleMessages.some((message) => message.id === streamingMessageId && message.sender === 'bot')
    );
  }, [streamingMessageId, visibleMessages]);

  const hasAnimatingBotMessage = useMemo(() => {
    return Boolean(
      animatingBotMessageId &&
      visibleMessages.some((message) => message.id === animatingBotMessageId && message.sender === 'bot')
    );
  }, [animatingBotMessageId, visibleMessages]);

  useEffect(() => {
    if (!currentChatId || visibleMessages.length === 0) return;
    clearChatSendPreparing(currentChatId);
  }, [clearChatSendPreparing, currentChatId, visibleMessages.length]);

  useEffect(() => {
    if (!transientChatError) return;
    const recovered = visibleMessages.some(
      (message) => message.id === transientChatError.botMessageId && message.sender === 'bot'
    );
    if (recovered) {
      setTransientChatError(null);
    }
  }, [transientChatError, visibleMessages]);

  useEffect(() => {
    if (!hasStreamingBotMessage || typingChatId !== currentChatId) return;
    setTypingChatId(null);
    setTypingStartTime(null);
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }, [currentChatId, hasStreamingBotMessage, typingChatId]);

  useEffect(() => {
    if (!currentChatId || messagesLoadState !== 'ready' || !visibleMessages.length) return;

    const visibleBotMessageIds = new Set(
      visibleMessages
        .filter((message) => message.sender === 'bot')
        .map((message) => message.id)
    );

    expectedBotMessageChatIdsRef.current.forEach((expectedChatId, botMessageId) => {
      if (expectedChatId !== currentChatId) return;
      if (!leftChatBeforeBotResponseIdsRef.current.has(botMessageId)) return;

      if (visibleBotMessageIds.has(botMessageId)) {
        pendingBotAnimationIdsRef.current.delete(botMessageId);
        completedBotAnimationIdsRef.current.add(botMessageId);
        returnedUnreadBotMessageIdsRef.current.add(botMessageId);
      } else {
        leftChatBeforeBotResponseIdsRef.current.delete(botMessageId);
      }
    });
  }, [currentChatId, messagesLoadState, visibleMessages]);

  useEffect(() => {
    if (!currentChatId || !visibleMessages.length) return;
    if (
      animatingBotMessageId
      && visibleMessages.some((message) => message.id === animatingBotMessageId && message.sender === 'bot')
    ) {
      return;
    }

    const nextMessageToAnimate = visibleMessages.find(
      (message) =>
        message.sender === 'bot'
        && pendingBotAnimationIdsRef.current.has(message.id)
        && !completedBotAnimationIdsRef.current.has(message.id)
    );

    if (!nextMessageToAnimate) return;

    pendingBotAnimationIdsRef.current.delete(nextMessageToAnimate.id);
    setAnimatingBotMessageId(nextMessageToAnimate.id);
    setStreamingMessageId(nextMessageToAnimate.id);
    shouldAutoFollowMessagesRef.current = !userPausedAutoFollowRef.current;

    window.requestAnimationFrame(() => {
      scrollMessagesToBottom('auto');
    });
  }, [animatingBotMessageId, currentChatId, scrollMessagesToBottom, visibleMessages]);

  // 4. Scroll Automático & Inteligente
  useEffect(() => {
    if (!visibleMessages.length) return;

    const lastMessage = visibleMessages[visibleMessages.length - 1];
    const isBotTyping = typingChatId === currentChatId;
    const isBotResponseActive = Boolean(
      (streamingMessageId && visibleMessages.some((message) => message.id === streamingMessageId && message.sender === 'bot'))
      || (animatingBotMessageId && visibleMessages.some((message) => message.id === animatingBotMessageId && message.sender === 'bot'))
      || activeDocumentGeneration
    );
    const hasChatChanged = lastChatIdRef.current !== currentChatId;
    const isLastMessageFromUser = lastMessage.sender === 'user';
    const isUpgradePrompt = lastMessage.sender === 'bot' && lastMessage.action === 'upgrade';
    const returnedUnreadMessageIds = visibleMessages
      .filter((message) => message.sender === 'bot' && returnedUnreadBotMessageIdsRef.current.has(message.id))
      .map((message) => message.id);
    const returnedUnreadMessageId = returnedUnreadMessageIds[0] || null;
    const shouldForceInitialScroll = Boolean(
      currentChatId &&
      pendingInitialScrollChatIdRef.current === currentChatId &&
      messagesChatId === currentChatId &&
      messagesLoadState === 'ready'
    );

    // Si el chat ha cambiado, actualizamos el ref
    if (hasChatChanged) {
      lastChatIdRef.current = currentChatId;
    }

    if (shouldForceInitialScroll) {
      pendingInitialScrollChatIdRef.current = null;

      if (returnedUnreadMessageId) {
        userPausedAutoFollowRef.current = true;
        shouldAutoFollowMessagesRef.current = false;

        returnedUnreadMessageIds.forEach((messageId) => {
          returnedUnreadBotMessageIdsRef.current.delete(messageId);
          leftChatBeforeBotResponseIdsRef.current.delete(messageId);
          expectedBotMessageChatIdsRef.current.delete(messageId);
          completedBotAnimationIdsRef.current.add(messageId);
        });
        setStreamingMessageId((current) => (
          current && returnedUnreadMessageIds.includes(current) ? null : current
        ));
        setAnimatingBotMessageId((current) => (
          current && returnedUnreadMessageIds.includes(current) ? null : current
        ));

        window.requestAnimationFrame(() => {
          scrollMessageToTop(returnedUnreadMessageId);
        });

        window.setTimeout(() => {
          scrollMessageToTop(returnedUnreadMessageId);
        }, 120);

        return;
      }

      userPausedAutoFollowRef.current = false;
      shouldAutoFollowMessagesRef.current = true;

      window.requestAnimationFrame(() => {
        scrollMessagesToBottom('auto', true);
      });

      window.setTimeout(() => {
        scrollMessagesToBottom('auto', true);
      }, 120);

      return;
    }

    if (isLastMessageFromUser || isUpgradePrompt) {
      userPausedAutoFollowRef.current = false;
      shouldAutoFollowMessagesRef.current = true;
    }

    // SI EL STREAMING ACABA DE TERMINAR (ya no hay streamingMessageId pero antes sí lo había)
    // No scrolleamos para no interrumpir la lectura del usuario.
    if (!streamingMessageId && !isBotTyping && !isBotResponseActive && !hasChatChanged && !isLastMessageFromUser && !isUpgradePrompt) {
      return;
    }

    // Si el bot está escribiendo o haciendo streaming, scrolleamos al fondo
    if (isBotTyping || isBotResponseActive) {
      scrollMessagesToBottom('auto', !userPausedAutoFollowRef.current);
      return;
    }

    const age = (Date.now() - new Date(lastMessage.timestamp).getTime()) / 1000;
    if (!hasChatChanged || (lastMessage.sender === 'bot' && age <= 30) || isLastMessageFromUser || isUpgradePrompt) {
      scrollMessagesToBottom('smooth', isLastMessageFromUser || isUpgradePrompt);
    }
  }, [activeDocumentGeneration, visibleMessages, typingChatId, streamingMessageId, animatingBotMessageId, currentChatId, messagesLoadState, messagesChatId, scrollMessageToTop, scrollMessagesToBottom]);

  const handleNewChat = () => {
    if (!profileCompleted) return;
    composerDrafts.deactivate();
    setNewDraftId((previous) => previous + 1);

    // Conserva el foco cómodo en escritorio; en móvil escribir requiere tocar la entrada.
    focusChatComposer();

    markPendingResponseLeftChat(currentChatIdRef.current);
    localOfflineMessages
      .filter((message) => message.localChatId === null)
      .forEach((message) => {
        scheduleObjectUrlRevoke(message.imageUrl || null);
        scheduleImageUrlsRevoke(message.imageUrls);
        scheduleObjectUrlRevoke(message.audioUrl || null);
      });
    clearLocalOptimisticAttachments();
    commitPendingOptimisticMessage(null);
    setCurrentChatId(null);
    setReasoningPreference('normal');
    setReasoningError(null);
    currentChatIdRef.current = null;
    setMessages([]);
    setMessagesChatId(null);
    userPausedAutoFollowRef.current = false;
    shouldAutoFollowMessagesRef.current = true;
    setLocalOfflineMessages((current) => current.filter((message) => message.localChatId !== null));
    setMessagesLoadState('idle');
    setSidebarOpen(false);
    setTypingChatId(null);
    setTransientChatError(null);
    activeGenerationRef.current = null;
    setPreparingSendChatIds(new Set());
    window.requestAnimationFrame(focusChatComposer);
  };

  const handleSelectChat = useCallback(async (chatId: string) => {
    if (!profileCompleted) return;

    if (chatId !== currentChatIdRef.current) {
      composerDrafts.deactivate();
      setReasoningPreference('normal');
      setReasoningError(null);
      markPendingResponseLeftChat(currentChatIdRef.current);
      clearLocalOptimisticAttachments();
      pendingInitialScrollChatIdRef.current = chatId;
      userPausedAutoFollowRef.current = false;
      shouldAutoFollowMessagesRef.current = true;
      setMessages([]);
      setMessagesChatId(null);
      setMessagesLoadState('loading');
    } else {
      userPausedAutoFollowRef.current = false;
      shouldAutoFollowMessagesRef.current = true;
      window.requestAnimationFrame(() => scrollMessagesToBottom('auto', true));
    }
    currentChatIdRef.current = chatId;
    setCurrentChatId(chatId);
  }, [clearLocalOptimisticAttachments, composerDrafts, markPendingResponseLeftChat, profileCompleted, scrollMessagesToBottom]);

  useEffect(() => {
    if (!user || !profileCompleted || !currentChatId) return;
    return onSnapshot(doc(db, CHAT_COLLECTION, currentChatId), (snapshot) => {
      setReasoningPreference(normalizeReasoningPreference(snapshot.data()?.reasoningPreference));
    }, (error) => {
      logTechnicalError(error, 'App:onSnapshot:reasoningPreference');
      setReasoningError('No he podido recuperar el nivel de razonamiento.');
    });
  }, [currentChatId, profileCompleted, user]);

  const completeBotAnimation = useCallback((botMessageId: string) => {
    pendingBotAnimationIdsRef.current.delete(botMessageId);
    completedBotAnimationIdsRef.current.add(botMessageId);
    expectedBotMessageChatIdsRef.current.delete(botMessageId);
    leftChatBeforeBotResponseIdsRef.current.delete(botMessageId);
    returnedUnreadBotMessageIdsRef.current.delete(botMessageId);
    setAnimatingBotMessageId((current) => (current === botMessageId ? null : current));
    setStreamingMessageId((current) => (current === botMessageId ? null : current));
  }, []);

  const prepareExpectedBotMessageAnimation = (chatId: string, botMessageId: string) => {
    if (completedBotAnimationIdsRef.current.has(botMessageId)) return;

    expectedBotMessageChatIdsRef.current.set(botMessageId, chatId);
    pendingBotAnimationIdsRef.current.add(botMessageId);
    setStreamingMessageId(botMessageId);

    if (currentChatIdRef.current === chatId && !userPausedAutoFollowRef.current) {
      shouldAutoFollowMessagesRef.current = true;
    } else if (currentChatIdRef.current !== chatId) {
      leftChatBeforeBotResponseIdsRef.current.add(botMessageId);
    }
  };

  const markBotMessageAnimatable = (chatId: string, botMessageId: string) => {
    prepareExpectedBotMessageAnimation(chatId, botMessageId);
  };

  const createOfflineMessage = (
    text: string | undefined,
    images: File[],
    audio: Blob | null,
    localChatId: string | null,
    chatMentions: ChatMention[] = []
  ) => {
    const messageId = createLocalOfflineMessageId();
    const localImageUrls = images.map((image) => URL.createObjectURL(image));
    const localAudioUrl = audio ? URL.createObjectURL(audio) : undefined;

    retryPayloadsRef.current[messageId] = {
      text: text?.trim() ? text : undefined,
      images,
      audio,
      document: null,
      chatMentions
    };

    const localMessage: LocalOfflineMessage = {
      id: messageId,
      localChatId,
      sender: 'user',
      text: text?.trim() || undefined,
      chatMentions,
      imageUrls: localImageUrls,
      audioUrl: localAudioUrl,
      generationStatus: 'failed',
      failureKind: 'connection',
      timestamp: new Date().toISOString()
    };

    setLocalOfflineMessages((current) => [...current, localMessage]);
    setSidebarOpen(false);
    setTransientChatError(null);
  };

  const removeLocalOfflineMessage = (message: Message, delayMs = 60_000) => {
    scheduleObjectUrlRevoke(message.imageUrl || null, delayMs);
    scheduleImageUrlsRevoke(message.imageUrls, delayMs);
    scheduleObjectUrlRevoke(message.audioUrl || null, delayMs);
    setLocalOfflineMessages((current) => current.filter((item) => item.id !== message.id));
  };

  const markMessageFailed = async (
    chatId: string,
    userMessageId: string,
    failureKind: Message['failureKind'] = 'technical'
  ) => {
    try {
      await updateDoc(doc(db, CHAT_COLLECTION, chatId, 'messages', userMessageId), {
        isUploading: false,
        generationStatus: 'failed',
        failureKind
      });
    } catch (error) {
      console.warn('No se pudo marcar el mensaje como fallido.', error);
    }
  };

  const addMaintenanceMessage = async (chatId: string) => {
    await addDoc(collection(db, CHAT_COLLECTION, chatId, 'messages'), {
      sender: 'bot',
      text: MAINTENANCE_MESSAGE,
      timestamp: serverTimestamp()
    });
  };

  const setMessageRetrying = (messageId: string, isRetrying: boolean) => {
    setRetryingMessageIds((current) => {
      const next = new Set(current);
      if (isRetrying) {
        next.add(messageId);
      } else {
        next.delete(messageId);
      }
      return next;
    });
  };

  const markGenerationCanceled = async (chatId: string, userMessageId: string, botMessageId?: string) => {
    const nextCanceled = new Set(canceledClientMessageIdsRef.current);
    nextCanceled.add(userMessageId);
    canceledClientMessageIdsRef.current = nextCanceled;
    setLocallyCanceledMessageIds(nextCanceled);

    await updateDoc(doc(db, CHAT_COLLECTION, chatId, 'messages', userMessageId), {
      generationStatus: 'canceled',
      failureKind: deleteField()
    }).catch((error) => {
      console.warn('No se pudo marcar la generación como cancelada.', error);
    });

    if (user) {
      void cancelMessageGeneration({
        chatId,
        userId: user.uid,
        clientMessageId: userMessageId,
        botMessageId
      }).catch((error) => {
        console.warn('No se pudo cancelar la generación en servidor.', error);
      });
    }
  };

  const retryOfflineMessage = async (message: LocalOfflineMessage) => {
    if (previewSendingDisabled(usagePreview?.state)) return;
    if (economicSendingDisabled(economicBilling.status, Boolean(usagePreview?.state), economicBilling.recoveryRequired)) return;
    if (!user || !profileCompleted || retryingMessageIds.has(message.id)) return;

    if (!isOnline) {
      return;
    }

    const retryPayload = retryPayloadsRef.current[message.id];
    const payloadText = retryPayload?.text ?? (message.text?.trim() ? message.text : undefined);
    const startedAt = Date.now();
    const botMessageId = getPersistedBotMessageId(message.id);
    const titleText = payloadText || (retryPayload?.images.length ? 'Imagen enviada' : 'Audio enviado');
    const autoTitle = titleText.split(' ').slice(0, 5).join(' ') + (titleText.split(' ').length > 5 ? '...' : '');
    let activeChatId = message.localChatId;
    let keepTypingUntilBotMessage = false;
    let pendingUploadedImageUrls: string[] = [];

    setMessageRetrying(message.id, true);
    setTransientChatError(null);

    try {
      if (!activeChatId) {
        activeChatId = await createChat(autoTitle, getAuthorizedReasoningPreference(reasoningPreference, userTier));
        markChatSendPreparing(activeChatId);
        setCurrentChatId(activeChatId);
        currentChatIdRef.current = activeChatId;
      } else {
        markChatSendPreparing(activeChatId);
        await updateDoc(doc(db, CHAT_COLLECTION, activeChatId), {
          updatedAt: serverTimestamp()
        });
      }

      const usageCheck = await economicBilling.refresh() === 'legacy'
        ? await checkUsageLimit(user.uid, user.email) : { allowed: true };
      if (!usageCheck.allowed) {
        await addDoc(collection(db, CHAT_COLLECTION, activeChatId, 'messages'), {
          sender: 'bot',
          text: '🔒 Has alcanzado el límite de mensajes de tu Plan.\n\nPara continuar disfrutando de Juris sin interrupciones, mejora tu plan ahora.',
          action: 'upgrade',
          timestamp: serverTimestamp()
        });
        removeLocalOfflineMessage(message);
        return;
      }

      const userMessageRef = doc(db, CHAT_COLLECTION, activeChatId, 'messages', message.id);
      await setDoc(userMessageRef, {
        sender: 'user',
        text: payloadText || null,
        chatMentions: message.chatMentions || [],
        imageUrl: null,
        imageUrls: [],
        audioUrl: null,
        isUploading: Boolean(retryPayload?.images.length || retryPayload?.audio),
        generationStatus: 'pending',
        timestamp: serverTimestamp()
      });

      if (message.imageUrls?.length || message.audioUrl) {
        preserveLocalOptimisticAttachment(message.id, {
          ...(message.imageUrls?.length ? { imageUrls: message.imageUrls } : {}),
          ...(message.audioUrl ? { audioUrl: message.audioUrl } : {})
        });
      }
      removeLocalOfflineMessage(message, 5 * 60_000);

      setTypingChatId(activeChatId);
      setTypingStartTime(startedAt);
      prepareExpectedBotMessageAnimation(activeChatId, botMessageId);
      activeGenerationRef.current = {
        chatId: activeChatId,
        userMessageId: message.id,
        botMessageId
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
        chatId: activeChatId,
        startTime: startedAt,
        userMessageId: message.id,
        botMessageId
      }));

      let payloadImageUrls: string[] = [];
      let payloadAudioUrl: string | undefined;

      if (retryPayload?.images.length) {
        if (!areSupportedChatImages(retryPayload.images)) {
          await markMessageFailed(activeChatId, message.id, 'unsupported_file');
          return;
        }
        payloadImageUrls = await uploadChatImages(retryPayload.images, activeChatId);
        pendingUploadedImageUrls = payloadImageUrls;
      }

      if (retryPayload?.audio) {
        if (!isSupportedChatAudioBlob(retryPayload.audio)) {
          await markMessageFailed(activeChatId, message.id, 'unsupported_file');
          return;
        }
        payloadAudioUrl = await uploadFileToFirebase(retryPayload.audio, 'audio.webm', currentChatId);
      }

      if (retryPayload?.images.length || retryPayload?.audio) {
        await updateDoc(userMessageRef, {
          imageUrls: payloadImageUrls,
          audioUrl: payloadAudioUrl || null,
          isUploading: false
        });
        pendingUploadedImageUrls = [];
      }

      const payload: ChatMessagePayload = {
        chatId: activeChatId,
        userId: user.uid,
        clientMessageId: message.id,
        message: payloadText,
        chatMentions: message.chatMentions,
        imageUrls: payloadImageUrls,
        audioUrl: payloadAudioUrl,
        timestamp: new Date().toISOString()
      };

      if (!payload.message && !payload.imageUrls?.length && !payload.audioUrl) {
        await markMessageFailed(activeChatId, message.id, 'connection');
        return;
      }

      keepTypingUntilBotMessage = await submitPreparedMessage(
        activeChatId,
        userMessageRef,
        message.id,
        botMessageId,
        payload
      );
      if (keepTypingUntilBotMessage) {
        delete retryPayloadsRef.current[message.id];
      }
    } catch (error: any) {
      if (pendingUploadedImageUrls.length) await deleteUploadedChatImages(pendingUploadedImageUrls);
      if (error.name !== 'AbortError' && activeChatId) {
        if (isMaintenanceErrorKind(getApiErrorKind(error))) {
          updateServiceStatus('maintenance');
          await markMessageFailed(activeChatId, message.id, 'connection');
          await addMaintenanceMessage(activeChatId);
          return;
        }

        await markMessageFailed(activeChatId, message.id, 'connection');
      }
    } finally {
      setMessageRetrying(message.id, false);

      if (!keepTypingUntilBotMessage) {
        setTypingChatId(null);
        setTypingStartTime(null);
        setStreamingMessageId(null);
        pendingBotAnimationIdsRef.current.delete(botMessageId);
        expectedBotMessageChatIdsRef.current.delete(botMessageId);
        leftChatBeforeBotResponseIdsRef.current.delete(botMessageId);
        returnedUnreadBotMessageIdsRef.current.delete(botMessageId);
        setAnimatingBotMessageId((current) => (current === botMessageId ? null : current));
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        clearChatSendPreparing(activeChatId);
      }

      if (activeGenerationRef.current?.userMessageId === message.id) {
        activeGenerationRef.current = null;
      }
    }
  };

  async function submitPreparedMessage(
    activeChatId: string,
    userMessageRef: DocumentReference,
    userMessageId: string,
    botMessageId: string,
    payload: ChatMessagePayload,
    onAccepted?: () => void
  ) {
    abortControllerRef.current = new AbortController();
    const responseData = await sendChatMessage(payload, abortControllerRef.current.signal).catch(error => {
      if (isEconomicAdmissionError(error)) {
        economicBilling.rejectAdmission(getApiErrorKind(error) === 'economic_billing_unavailable');
      }
      throw error;
    });
    abortControllerRef.current = null;
    if (responseData.accepted) {
      economicBilling.acknowledgeMessage();
      onAccepted?.();
    }

    const responseText = responseData.text;
    const expectedBotMessageId = responseData.botMessageId || botMessageId;
    const persistedBotMessageId = responseData.responsePersisted ? expectedBotMessageId : null;

    if (
      responseData.generationCanceled ||
      canceledClientMessageIdsRef.current.has(userMessageId) ||
      sendCancelledRef.current
    ) {
      if (activeGenerationRef.current?.userMessageId === userMessageId) {
        activeGenerationRef.current = null;
      }
      return false;
    }

    if (responseData.accepted || responseData.responsePersisted || responseText?.trim()) {
      acknowledgePreviewMessage();
    }

    if (responseData.accepted) {
      prepareExpectedBotMessageAnimation(activeChatId, expectedBotMessageId);

      const callbackResult = await waitForPersistedBotMessage(
        activeChatId,
        userMessageId,
        expectedBotMessageId,
        ASYNC_CALLBACK_RESPONSE_TIMEOUT_MS
      );

      if (callbackResult === 'found') {
        markBotMessageAnimatable(activeChatId, expectedBotMessageId);
        if (activeGenerationRef.current?.userMessageId === userMessageId) {
          activeGenerationRef.current = null;
        }
        return true;
      }

      if (callbackResult === 'canceled' || callbackResult === 'failed' || sendCancelledRef.current) {
        if (activeGenerationRef.current?.userMessageId === userMessageId) {
          activeGenerationRef.current = null;
        }
        return false;
      }

      await markMessageFailed(activeChatId, userMessageId, 'connection');
      if (activeGenerationRef.current?.userMessageId === userMessageId) {
        activeGenerationRef.current = null;
      }
      return false;
    }

    if (persistedBotMessageId) {
      markBotMessageAnimatable(activeChatId, persistedBotMessageId);
      if (activeGenerationRef.current?.userMessageId === userMessageId) {
        activeGenerationRef.current = null;
      }
      return true;
    }

    await setDoc(doc(db, CHAT_COLLECTION, activeChatId, 'messages', botMessageId), {
      sender: 'bot',
      text: responseText,
      sourceClientMessageId: userMessageId,
      timestamp: serverTimestamp()
    });

    await updateDoc(userMessageRef, {
      generationStatus: 'completed',
      failureKind: deleteField()
    });

    if (!responseData.usageIncremented) {
      await recordCompletedMessageUsage(activeChatId, userMessageId, botMessageId);
    }

    markBotMessageAnimatable(activeChatId, botMessageId);

    if (activeGenerationRef.current?.userMessageId === userMessageId) {
      activeGenerationRef.current = null;
    }

    return true;
  }

  const handleRetryMessage = async (message: Message) => {
    if (isPreviewSendingDisabled) return;
    if (!profileCompleted) return;

    const localOfflineMessage = localOfflineMessages.find((item) => item.id === message.id);
    if (localOfflineMessage) {
      await retryOfflineMessage(localOfflineMessage);
      return;
    }

    if (!user || !currentChatId || message.sender !== 'user' || message.generationStatus !== 'failed') return;
    if (retryingMessageIds.has(message.id)) return;

    if (economicBilling.status) {
      const retryOwner = user.uid, retryChatId = currentChatId, originalRetryId = message.id;
      setMessageRetrying(originalRetryId, true);
      try {
        const priorId = message.id;
        const response = await fetch('/api/billing/retry', { method: 'POST',
          headers: { Authorization: `Bearer ${await auth.currentUser!.getIdToken()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ chatId: currentChatId, clientMessageId: priorId }) });
        if (!response.ok) throw new Error('retry_unavailable');
        const prepared = await response.json();
        if (auth.currentUser?.uid !== retryOwner || currentChatIdRef.current !== retryChatId) return;
        if (!/^[a-f0-9]{64}$/.test(prepared.clientMessageId)) throw new Error('retry_invalid');
        const retryRef = doc(db, CHAT_COLLECTION, currentChatId, 'messages', prepared.clientMessageId);
        if ((await getDoc(retryRef)).exists()) return;
        await setDoc(retryRef, { sender: 'user', text: message.text || null, chatMentions: message.chatMentions || [],
          ...getRetryImageFields(message, getPersistedImageUrls(message)), audioUrl: getPersistedAttachmentUrl(message.audioUrl) || null,
          attachmentIds: message.attachmentIds || [], attachmentName: message.attachmentName || null,
          generationStatus: 'failed', timestamp: serverTimestamp() });
        retryPayloadsRef.current[prepared.clientMessageId] = retryPayloadsRef.current[priorId];
        message = { ...message, id: prepared.clientMessageId };
      } catch {
        if (auth.currentUser?.uid !== retryOwner || currentChatIdRef.current !== retryChatId) return;
        setTransientChatError({ chatId: currentChatId, botMessageId: getPersistedBotMessageId(message.id),
          message: { id: `retry_error_${message.id}`, sender: 'bot', timestamp: new Date().toISOString(),
            text: 'No se pudo preparar el reintento. Espera a que termine la ejecución anterior y vuelve a intentarlo.' } });
        return;
      } finally { setMessageRetrying(originalRetryId, false); }
    }

    const retryPayload = retryPayloadsRef.current[message.id];
    const payloadText = retryPayload?.text ?? (message.text?.trim() ? message.text : undefined);
    const botMessageId = getPersistedBotMessageId(message.id);
    const userMessageRef = doc(db, CHAT_COLLECTION, currentChatId, 'messages', message.id);
    const startedAt = Date.now();
    let keepTypingUntilBotMessage = false;
    let payloadImageUrls = getPersistedImageUrls(message);
    let pendingUploadedImageUrls: string[] = [];
    let replacedImageUrls: string[] = [];
    let payloadAudioUrl = getPersistedAttachmentUrl(message.audioUrl);
    let attachmentIds = message.attachmentIds?.length
      ? message.attachmentIds
      : retryPayload?.attachmentIds;

    sendCancelledRef.current = false;
    setMessageRetrying(message.id, true);
    setTypingChatId(currentChatId);
    setTypingStartTime(startedAt);
    prepareExpectedBotMessageAnimation(currentChatId, botMessageId);
    setTransientChatError(null);
    activeGenerationRef.current = {
      chatId: currentChatId,
      userMessageId: message.id,
      botMessageId
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
      chatId: currentChatId,
      startTime: startedAt,
      userMessageId: message.id,
      botMessageId
    }));

    try {
      await updateDoc(userMessageRef, {
        generationStatus: 'pending',
        failureKind: deleteField(),
        isUploading: Boolean(
          (retryPayload?.images.length && payloadImageUrls.length !== retryPayload.images.length)
          || (retryPayload?.audio && !payloadAudioUrl)
          || (retryPayload?.document && !attachmentIds?.length)
        )
      });

      if (retryPayload?.images.length && payloadImageUrls.length !== retryPayload.images.length) {
        if (!areSupportedChatImages(retryPayload.images)) {
          await markMessageFailed(currentChatId, message.id, 'unsupported_file');
          return;
        }

        const replacementUrls = await uploadChatImages(retryPayload.images, currentChatId);
        pendingUploadedImageUrls = replacementUrls;
        replacedImageUrls = payloadImageUrls;
        payloadImageUrls = replacementUrls;
      }

      if (!payloadAudioUrl && retryPayload?.audio) {
        if (!isSupportedChatAudioBlob(retryPayload.audio)) {
          await markMessageFailed(currentChatId, message.id, 'unsupported_file');
          return;
        }

        payloadAudioUrl = await uploadFileToFirebase(retryPayload.audio, 'audio.webm');
      }

      if (!attachmentIds?.length && retryPayload?.document) {
        attachmentIds = [await registerDiligenceAttachment(currentChatId, retryPayload.document)];
      }

      if (retryPayload?.images.length || retryPayload?.audio || retryPayload?.document) {
        await updateDoc(userMessageRef, {
          ...getRetryImageFields(message, payloadImageUrls),
          audioUrl: payloadAudioUrl || null,
          attachmentIds: attachmentIds || [],
          attachmentName: retryPayload?.document?.name || message.attachmentName || null,
          isUploading: false
        });
        pendingUploadedImageUrls = [];
        if (replacedImageUrls.length) await deleteUploadedChatImages(replacedImageUrls);
      }

      const payload: ChatMessagePayload = {
        chatId: currentChatId,
        userId: user.uid,
        clientMessageId: message.id,
        message: payloadText,
        chatMentions: message.chatMentions,
        ...getRetryImageFields(message, payloadImageUrls),
        audioUrl: payloadAudioUrl,
        attachmentIds,
        timestamp: new Date().toISOString()
      };

      if (!payload.message && !payload.imageUrl && !payload.imageUrls?.length && !payload.audioUrl && !payload.attachmentIds?.length) {
        await markMessageFailed(currentChatId, message.id, 'connection');
        return;
      }

      keepTypingUntilBotMessage = await submitPreparedMessage(
        currentChatId,
        userMessageRef,
        message.id,
        botMessageId,
        payload
      );
      if (keepTypingUntilBotMessage) {
        delete retryPayloadsRef.current[message.id];
      }
    } catch (error: any) {
      if (pendingUploadedImageUrls.length) await deleteUploadedChatImages(pendingUploadedImageUrls);
      if (error.name === 'AbortError') {
        return;
      }

      const apiErrorKind = getApiErrorKind(error);

      if (isEconomicAdmissionError(error)) {
        await markMessageFailed(currentChatId, message.id, 'technical');
        return;
      }

      if (isMaintenanceErrorKind(apiErrorKind)) {
        updateServiceStatus('maintenance');
        await markMessageFailed(currentChatId, message.id, 'connection');
        await addMaintenanceMessage(currentChatId);
        return;
      }

      if (apiErrorKind === 'usage_limit_reached') {
        await markMessageFailed(currentChatId, message.id, 'technical');
        await addDoc(collection(db, CHAT_COLLECTION, currentChatId, 'messages'), {
          sender: 'bot',
          text: 'Has alcanzado el límite de mensajes de tu Plan.\n\nPara continuar disfrutando de Juris sin interrupciones, mejora tu plan ahora.',
          action: 'upgrade',
          timestamp: serverTimestamp()
        });
        return;
      }

      console.warn('No se pudo reintentar el envio del mensaje.', error);
      await markMessageFailed(
        currentChatId,
        message.id,
        isDiligenceUploadFailure(error) ? 'document_upload' : 'connection'
      );
    } finally {
      setMessageRetrying(message.id, false);

      if (!keepTypingUntilBotMessage) {
        setTypingChatId(null);
        setTypingStartTime(null);
        setStreamingMessageId(null);
        pendingBotAnimationIdsRef.current.delete(botMessageId);
        expectedBotMessageChatIdsRef.current.delete(botMessageId);
        leftChatBeforeBotResponseIdsRef.current.delete(botMessageId);
        returnedUnreadBotMessageIdsRef.current.delete(botMessageId);
        setAnimatingBotMessageId((current) => (current === botMessageId ? null : current));
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }

      if (activeGenerationRef.current?.userMessageId === message.id) {
        activeGenerationRef.current = null;
      }
    }
  };

  const canRetryMessage = (message: Message) => {
    if (!isOnline) return false;
    if (serviceStatus === 'maintenance') return false;

    if (localOfflineMessages.some((item) => item.id === message.id)) {
      return message.generationStatus === 'failed' && message.failureKind === 'connection';
    }

    if (
      message.sender !== 'user'
      || message.generationStatus !== 'failed'
      || !['connection', 'generation', 'document_upload'].includes(message.failureKind || '')
    ) {
      return false;
    }

    const retryPayload = retryPayloadsRef.current[message.id];
    return Boolean(
      message.text?.trim()
      || getPersistedImageUrls(message).length
      || getPersistedAttachmentUrl(message.audioUrl)
      || message.attachmentIds?.length
      || retryPayload?.text
      || retryPayload?.images.length
      || retryPayload?.audio
      || retryPayload?.document
      || retryPayload?.attachmentIds?.length
    );
  };

  const handleSendMessage = async (text: string | undefined, images: File[], audio: Blob | null, documentFile: File | null = null, chatMentions: ChatMention[] = []) => {
    if (isPreviewSendingDisabled) return;
    if (!user || !profileCompleted) return;
    if (isSavingReasoning) return;
    if (pendingOptimisticMessageRef.current) return;
    const submittedDraft = composerDraftBinding?.read();
    let submittedBinding = composerDraftBinding;
    const clearSubmittedDraft = () => {
      if (!submittedDraft || !submittedBinding || auth.currentUser?.uid !== user.uid) return;
      if (composerDrafts.clearIfUnchanged(user.uid, submittedBinding, submittedDraft)) {
        setComposerDraftRevision(value => value + 1);
      }
    };

    if (!areSupportedChatImages(images) || !isSupportedChatAudioBlob(audio)) {
      console.warn('Archivo adjunto no admitido. Se evita la subida a Firebase Storage.');
      return;
    }

    if (!isOnline) {
      if (documentFile) return;
      createOfflineMessage(text, images, audio, currentChatId, chatMentions);
      // The offline queue now owns the files, mentions and retry payload.
      clearSubmittedDraft();
      return;
    }

    sendCancelledRef.current = false;
    userPausedAutoFollowRef.current = false;
    shouldAutoFollowMessagesRef.current = true;

    // Si la barra estaba abierta, la cerramos al enviar
    setSidebarOpen(false);

    let activeChatId = currentChatId;
    let pendingUserMessageId: string | null = null;
    let pendingBotMessageId: string | null = null;
    let keepTypingUntilBotMessage = false;
    const titleText = text ? text : (documentFile ? documentFile.name : (images.length ? 'Imagen enviada' : 'Audio enviado'));
    const autoTitle = titleText.split(' ').slice(0, 5).join(' ') + (titleText.split(' ').length > 5 ? '...' : '');
    const localImageUrls = images.map((image) => URL.createObjectURL(image));
    const localAudioUrl = audio ? URL.createObjectURL(audio) : null;
    const optimisticMessageId = doc(collection(db, CHAT_COLLECTION)).id;
    const sendStartedAt = Date.now();
    const optimisticMessage: PendingOptimisticMessage = {
      id: optimisticMessageId,
      localChatId: activeChatId,
      persistedMessageId: optimisticMessageId,
      sender: 'user',
      text: text?.trim() || undefined,
      chatMentions,
      imageUrls: localImageUrls,
      audioUrl: localAudioUrl || undefined,
      attachmentName: documentFile?.name,
      isUploading: Boolean(images.length || audio || documentFile),
      generationStatus: 'pending',
      timestamp: new Date().toISOString()
    };
    commitPendingOptimisticMessage(optimisticMessage);
    setTypingStartTime(sendStartedAt);
    if (activeChatId) {
      setTypingChatId(activeChatId);
    }

    try {
      if (!activeChatId) {
        activeChatId = await createChat(autoTitle, getAuthorizedReasoningPreference(reasoningPreference, userTier));
        if (auth.currentUser?.uid !== user.uid) return;
        markChatSendPreparing(activeChatId);
        if (composerDraftBinding && composerDrafts.promoteToChat(user.uid, composerDraftBinding, activeChatId)) {
          setCurrentChatId(activeChatId);
          currentChatIdRef.current = activeChatId;
        }
        submittedBinding = composerDrafts.bind(user.uid, activeChatId, 0);
        setTypingChatId(activeChatId);
        commitPendingOptimisticMessage({
          ...(pendingOptimisticMessageRef.current || optimisticMessage),
          localChatId: activeChatId
        });
      } else {
        markChatSendPreparing(activeChatId);
        await updateDoc(doc(db, CHAT_COLLECTION, activeChatId), {
          updatedAt: serverTimestamp()
        });
      }

      if (serviceStatusRef.current === 'maintenance') {
        const localAttachment: LocalOptimisticAttachment | null = localImageUrls.length || localAudioUrl
          ? {
            ...(localImageUrls.length ? { imageUrls: localImageUrls } : {}),
            ...(localAudioUrl ? { audioUrl: localAudioUrl } : {})
          }
          : null;

        const userMessageRef = doc(db, CHAT_COLLECTION, activeChatId, 'messages', optimisticMessageId);
        if (localAttachment) {
          preserveLocalOptimisticAttachment(userMessageRef.id, localAttachment);
        }
        await setDoc(userMessageRef, {
          sender: 'user',
          text: text || null,
          chatMentions,
          imageUrl: null,
          imageUrls: [],
          audioUrl: null,
          isUploading: false,
          generationStatus: 'completed',
          timestamp: serverTimestamp()
        });
        commitPendingOptimisticMessage({
          ...(pendingOptimisticMessageRef.current || optimisticMessage),
          id: userMessageRef.id,
          localChatId: activeChatId,
          persistedMessageId: userMessageRef.id
        });

        await addMaintenanceMessage(activeChatId);
        return;
      }

      const usageCheck = await economicBilling.refresh() === 'legacy'
        ? await checkUsageLimit(user.uid, user.email) : { allowed: true };

      if (!usageCheck.allowed) {
        commitPendingOptimisticMessage(null);
        scheduleImageUrlsRevoke(localImageUrls);
        scheduleObjectUrlRevoke(localAudioUrl);
        await addDoc(collection(db, CHAT_COLLECTION, activeChatId, 'messages'), {
          sender: 'bot',
          text: '🔒 Has alcanzado el límite de mensajes de tu Plan.\n\nPara continuar disfrutando de Juris sin interrupciones, mejora tu plan ahora.',
          action: 'upgrade',
          timestamp: serverTimestamp()
        });
        return;
      }

      setTypingChatId(activeChatId);
      const now = sendStartedAt;
      setTypingStartTime(now);

      // PERSISTIMOS EN LOCAL STORAGE PARA SURVIVIR REFRESH (F5)
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
        chatId: activeChatId,
        startTime: now
      }));

      // CREAMOS URLs LOCALES PARA EL ENVÍO OPTIMISTA
      const localAttachment: LocalOptimisticAttachment | null = localImageUrls.length || localAudioUrl
        ? {
          ...(localImageUrls.length ? { imageUrls: localImageUrls } : {}),
          ...(localAudioUrl ? { audioUrl: localAudioUrl } : {})
        }
        : null;

      // Firestore solo guarda el marcador; los blob: optimistas no son portables entre dispositivos.
      const userMessageRef = doc(db, CHAT_COLLECTION, activeChatId, 'messages', optimisticMessageId);
      if (localAttachment) {
        preserveLocalOptimisticAttachment(userMessageRef.id, localAttachment);
      }
      await setDoc(userMessageRef, {
        sender: 'user',
        text: text || null,
        chatMentions,
        imageUrl: null,
        imageUrls: [],
        audioUrl: null,
        attachmentName: documentFile?.name || null,
        isUploading: !!(images.length || audio || documentFile),
        generationStatus: 'pending',
        timestamp: serverTimestamp()
      });
      commitPendingOptimisticMessage({
        ...(pendingOptimisticMessageRef.current || optimisticMessage),
        localChatId: activeChatId
      });
      retryPayloadsRef.current[userMessageRef.id] = {
        text: text?.trim() ? text : undefined,
        images,
        audio,
        document: documentFile,
        chatMentions
      };
      pendingUserMessageId = userMessageRef.id;
      pendingBotMessageId = getPersistedBotMessageId(userMessageRef.id);
      activeGenerationRef.current = {
        chatId: activeChatId,
        userMessageId: userMessageRef.id,
        botMessageId: pendingBotMessageId
      };
      prepareExpectedBotMessageAnimation(activeChatId, pendingBotMessageId);
      setTransientChatError(null);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
        chatId: activeChatId,
        startTime: now,
        userMessageId: userMessageRef.id,
        botMessageId: pendingBotMessageId
      }));

      if (sendCancelledRef.current) {
        await markGenerationCanceled(activeChatId, userMessageRef.id, pendingBotMessageId);
        return;
      }

      let payloadImageUrls: string[] = [];
      let payloadAudioUrl: string | undefined;
      let attachmentIds: string[] | undefined;

      try {
        // --- OPTIMISTIC MARKER ---
        if (images.length) payloadImageUrls = await uploadChatImages(images, activeChatId);
        if (audio) payloadAudioUrl = await uploadFileToFirebase(audio, 'audio.webm', activeChatId);
        if (documentFile) attachmentIds = [await registerDiligenceAttachment(activeChatId, documentFile)];
        if (attachmentIds?.length) {
          retryPayloadsRef.current[userMessageRef.id].attachmentIds = attachmentIds;
        }

        // SI HABÍA ARCHIVO, ACTUALIZAMOS EL DOCUMENTO CON LA URL PUBLICA DE FIREBASE STORAGE
        if (images.length || audio || documentFile) {
          await updateDoc(userMessageRef, {
            imageUrls: payloadImageUrls,
            audioUrl: payloadAudioUrl || null,
            attachmentIds: attachmentIds || [],
            attachmentName: documentFile?.name || null,
            isUploading: false
          });
        }
      } catch (uploadError) {
        if (payloadImageUrls.length) await deleteUploadedChatImages(payloadImageUrls);
        console.error("Error al subir archivo a Firebase Storage:", uploadError);
        const uploadFailure = isDiligenceUploadFailure(uploadError) ? uploadError : null;
        logTechnicalError(uploadError, 'diligence:attachment_upload_failed', {
          tags: {
            feature: 'diligences',
            operation: 'upload_attachment',
            stage: uploadFailure?.stage || 'unknown',
            error_kind: uploadFailure?.errorKind || 'unknown',
            status_code: uploadFailure?.status,
            request_id: uploadFailure?.requestId
          }
        });

        await markMessageFailed(activeChatId, userMessageRef.id, 'document_upload');
        scheduleImageUrlsRevoke(localImageUrls, 5 * 60_000);
        scheduleObjectUrlRevoke(localAudioUrl, 5 * 60_000);
        window.setTimeout(() => {
          setLocalOptimisticAttachments((previous) => {
            const next = { ...previous };
            delete next[userMessageRef.id];
            localOptimisticAttachmentsRef.current = next;
            return next;
          });
        }, 5 * 60_000);
        return;
      }

      if (sendCancelledRef.current) return;
      if (!pendingUserMessageId || !pendingBotMessageId) {
        throw new Error('No se pudo preparar la recuperación de la respuesta.');
      }

      const payload: ChatMessagePayload = {
        chatId: activeChatId,
        userId: user.uid,
        clientMessageId: pendingUserMessageId,
        message: text?.trim() ? text : undefined,
        chatMentions,
        imageUrls: payloadImageUrls,
        audioUrl: payloadAudioUrl,
        attachmentIds,
        timestamp: new Date().toISOString(),
      };

      if (!payload.message && !payload.imageUrls?.length && !payload.audioUrl && !payload.attachmentIds?.length) {
        await markMessageFailed(activeChatId, pendingUserMessageId, 'technical');
        return;
      }

      keepTypingUntilBotMessage = await submitPreparedMessage(
        activeChatId,
        userMessageRef,
        pendingUserMessageId,
        pendingBotMessageId,
        payload,
        clearSubmittedDraft
      );
      if (keepTypingUntilBotMessage) {
        delete retryPayloadsRef.current[pendingUserMessageId];
      }
      return;

    } catch (error: any) {
      if (!pendingOptimisticMessageRef.current?.persistedMessageId) {
        commitPendingOptimisticMessage(null);
        scheduleImageUrlsRevoke(localImageUrls);
        scheduleObjectUrlRevoke(localAudioUrl);
      }
      if (error.name === 'AbortError') {
        console.log("Generación cancelada por el usuario");
        return;
      }
      const apiErrorKind = getApiErrorKind(error);

      if (isEconomicAdmissionError(error)) {
        if (activeChatId && pendingUserMessageId) await markMessageFailed(activeChatId, pendingUserMessageId, 'technical');
        return;
      }

      if (isMaintenanceErrorKind(apiErrorKind)) {
        updateServiceStatus('maintenance');
        if (activeChatId && pendingUserMessageId) {
          await markMessageFailed(activeChatId, pendingUserMessageId, 'connection');
        }
        if (activeChatId) {
          await addMaintenanceMessage(activeChatId);
        }
        return;
      }

      if (activeChatId && pendingUserMessageId && pendingBotMessageId && isRecoverableSendMessageError(error, abortControllerRef.current?.signal)) {
        console.warn("Respuesta perdida en red; intentando recuperar la copia persistida.", error);
        const recoveryResult = await waitForPersistedBotMessage(activeChatId, pendingUserMessageId, pendingBotMessageId);

        if (recoveryResult === 'found') {
          acknowledgePreviewMessage();
          if (!sendCancelledRef.current) {
            keepTypingUntilBotMessage = true;
            setStreamingMessageId(pendingBotMessageId);
          }
          if (activeGenerationRef.current?.userMessageId === pendingUserMessageId) {
            activeGenerationRef.current = null;
          }
          return;
        }

        if (recoveryResult === 'canceled' || recoveryResult === 'failed' || sendCancelledRef.current) {
          if (activeGenerationRef.current?.userMessageId === pendingUserMessageId) {
            activeGenerationRef.current = null;
          }
          return;
        }

        const networkErrorKind = getSendMessageNetworkErrorKind(error) || 'unknown_network_error';
        logTechnicalError(error, "api:sendChatMessage:network_unrecovered", {
          tags: {
            feature: 'chat',
            operation: 'send_message',
            error_kind: networkErrorKind,
            recovered: false,
            has_audio: Boolean(audio),
            has_image: images.length > 0,
            has_text: Boolean(text),
            has_client_message_id: true
          },
          extra: {
            errorName: error instanceof Error ? error.name : undefined,
            hasAudio: Boolean(audio),
            hasImage: images.length > 0,
            hasText: Boolean(text)
          },
          fingerprint: ['api:sendChatMessage:network_unrecovered', networkErrorKind]
        });

        await markMessageFailed(activeChatId, pendingUserMessageId, 'connection');
        return;

      }

      if (apiErrorKind === 'usage_limit_reached') {
        if (activeChatId && pendingUserMessageId) {
          await markMessageFailed(activeChatId, pendingUserMessageId, 'technical');
        }
        if (activeChatId) {
          await addDoc(collection(db, CHAT_COLLECTION, activeChatId, 'messages'), {
            sender: 'bot',
            text: 'Has alcanzado el límite de mensajes de tu Plan.\n\nPara continuar disfrutando de Juris sin interrupciones, mejora tu plan ahora.',
            action: 'upgrade',
            timestamp: serverTimestamp()
          });
        }
        return;
      }

      console.error("Error en flujo de mensaje:", error);
      logTechnicalError(error, "App:handleSendMessage");
      if (activeChatId && pendingUserMessageId) {
        await markMessageFailed(activeChatId, pendingUserMessageId);
      }
      if (activeChatId) {
        await addDoc(collection(db, CHAT_COLLECTION, activeChatId, 'messages'), {
          sender: 'bot',
          text: 'Lo siento, tuve un error técnico. Inténtalo de nuevo y si persiste, contacta con reporte.',
          action: 'report',
          timestamp: serverTimestamp()
        });
      }
    } finally {
      if (!keepTypingUntilBotMessage) {
        setTypingChatId(null);
        setTypingStartTime(null);
        setStreamingMessageId(null);
        if (pendingBotMessageId) {
          pendingBotAnimationIdsRef.current.delete(pendingBotMessageId);
          expectedBotMessageChatIdsRef.current.delete(pendingBotMessageId);
          leftChatBeforeBotResponseIdsRef.current.delete(pendingBotMessageId);
          returnedUnreadBotMessageIdsRef.current.delete(pendingBotMessageId);
          setAnimatingBotMessageId((current) => (current === pendingBotMessageId ? null : current));
        }
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        clearChatSendPreparing(activeChatId);
      }
      if (activeGenerationRef.current?.userMessageId === pendingUserMessageId) {
        activeGenerationRef.current = null;
      }
    }
  };

  const handleStopGeneration = async () => {
    const activeGeneration = activeGenerationRef.current;

    if (activeDocumentGeneration?.sourceClientMessageId && user && currentChatId) {
      sendCancelledRef.current = true;
      try {
        await cancelMessageGeneration({
          chatId: currentChatId,
          userId: user.uid,
          clientMessageId: activeDocumentGeneration.sourceClientMessageId,
          botMessageId: activeDocumentGeneration.id
        });
      } catch (error) {
        sendCancelledRef.current = false;
        console.warn('No se pudo cancelar la generación documental.', error);
        return;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      activeGenerationRef.current = null;
      setStreamingMessageId(null);
      if (activeGeneration) {
        pendingBotAnimationIdsRef.current.delete(activeGeneration.botMessageId);
        expectedBotMessageChatIdsRef.current.delete(activeGeneration.botMessageId);
        leftChatBeforeBotResponseIdsRef.current.delete(activeGeneration.botMessageId);
        returnedUnreadBotMessageIdsRef.current.delete(activeGeneration.botMessageId);
        setAnimatingBotMessageId((current) => (
          current === activeGeneration.botMessageId ? null : current
        ));
      }
      setTypingChatId(null);
      setTypingStartTime(null);
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      return;
    }

    sendCancelledRef.current = true;
    if (activeGeneration) {
      await markGenerationCanceled(activeGeneration.chatId, activeGeneration.userMessageId, activeGeneration.botMessageId);
      setTransientChatError((current) =>
        current?.botMessageId === activeGeneration.botMessageId ? null : current
      );
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    activeGenerationRef.current = null;
    setStreamingMessageId(null);
    if (activeGeneration) {
      pendingBotAnimationIdsRef.current.delete(activeGeneration.botMessageId);
      expectedBotMessageChatIdsRef.current.delete(activeGeneration.botMessageId);
      leftChatBeforeBotResponseIdsRef.current.delete(activeGeneration.botMessageId);
      returnedUnreadBotMessageIdsRef.current.delete(activeGeneration.botMessageId);
      setAnimatingBotMessageId((current) => (current === activeGeneration.botMessageId ? null : current));
    }
    setTypingChatId(null);
    setTypingStartTime(null);
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  };

  if (authLoading || (user && profileLoading)) {
    return <AppLoadingScreen />;
  }

  if (!user) {
    return <Login />;
  }

  if (localStorage.getItem('juris_account_deleted') === 'true') {
    return <AppLoadingScreen />;
  }

  if (!profileCompleted) {
    return <Onboarding user={user} onComplete={() => setProfileCompleted(true)} />;
  }

  if (isAdminPath(currentPath)) {
    return (
      <Suspense fallback={<AppLoadingScreen />}>
        {currentPath === '/admin/economic-lab'
          ? <EconomicLabPanel isAdmin={userTier === 'admin'} onBackToChat={handleBackToChat} />
          : <AdminDashboard isAdmin={userTier === 'admin'} onBackToChat={handleBackToChat} />}
      </Suspense>
    );
  }

  const isNewChat = (!currentChatId || messagesLoadState === 'ready') && isNewChatSurface({
    currentChatId,
    persistedMessageCount: messages.length,
    localOfflineMessageCount: countLocalMessagesForChat(localOfflineMessages, currentChatId),
    hasPendingMessage: Boolean(pendingOptimisticMessage)
  });
  const handleReasoningChange = async (nextPreference: ReasoningPreference) => {
    if (!canUseReasoningPreference(nextPreference, userTier) || isSavingReasoning || isInputLoading) return;
    setReasoningError(null);
    if (!currentChatId) {
      setReasoningPreference(nextPreference);
      return;
    }
    const targetChatId = currentChatId;
    setIsSavingReasoning(true);
    try {
      await updateDoc(doc(db, CHAT_COLLECTION, targetChatId), { reasoningPreference: nextPreference });
      if (currentChatIdRef.current === targetChatId) setReasoningPreference(nextPreference);
    } catch (error) {
      logTechnicalError(error, 'App:updateReasoningPreference');
      if (currentChatIdRef.current === targetChatId) setReasoningError('No he podido guardar el nivel. Inténtalo de nuevo.');
    } finally {
      setIsSavingReasoning(false);
    }
  };
  const currentTransientError = transientChatError?.chatId === currentChatId ? transientChatError.message : null;
  const isPreparingSendInCurrentChat = currentChatId ? preparingSendChatIds.has(currentChatId) : false;
  const isTypingCurrentChat = isTypingIndicatorScopedToChat({
    currentChatId,
    typingChatId,
    hasPendingMessage: Boolean(pendingOptimisticMessage),
    hasStartTime: Boolean(typingStartTime)
  });
  const hasActiveTypingIndicator = shouldShowTypingIndicator({
    scopedToCurrentChat: isTypingCurrentChat,
    hasStartTime: Boolean(typingStartTime),
    hasStreamingBotMessage,
    hasAnimatingBotMessage,
    hasTransientError: Boolean(currentTransientError),
    hasActiveDocumentGeneration: Boolean(activeDocumentGeneration)
  });

  return (
    <EconomicBillingContext.Provider value={economicBilling}>
    <UsagePreviewContext.Provider value={usagePreview}>
    <div className="flex bg-canvas overflow-clip font-sans" style={{ height: 'var(--juris-app-height, 100dvh)' }}>
      <Sidebar
        currentChatId={currentChatId}
        onSelectChat={handleSelectChat}
        onChatsChange={setMentionableChats}
        onChatsDeleted={(chatIds) => { if (user) composerDrafts.removeChats(user.uid, chatIds); }}
        onNewChat={handleNewChat}
        isOnline={isOnline}
        isOpen={sidebarOpen}
        onCloseMobile={() => setSidebarOpen(false)}
        onOpenProfile={() => openPanel('settings')}
        onOpenPlans={() => setShowPlansModal(true)}
        onOpenAdmin={handleOpenAdminDashboard}
      />

      <Suspense fallback={null}>
        {showProfileModal && <UserProfileModal onClose={closePanel} />}
        {showPlansModal && <PlansModal onClose={() => setShowPlansModal(false)} />}
      </Suspense>

      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setReportModalOpen(false)}
        user={user}
        chatId={currentChatId}
        messages={visibleMessages}
      />

      <DiligenceTemplateLibrary isOpen={showTemplateLibrary} onClose={closePanel} />

      {/* MODAL POST-CHECKOUT */}
      {checkoutResult && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm motion-fade-through-fast">
          <div
            ref={checkoutDialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={checkoutResult === 'success' ? 'Suscripcion activada' : 'Pago no completado'}
            tabIndex={-1}
            className="bg-panel rounded-2xl shadow-2xl w-full max-w-sm max-h-[90dvh] overflow-y-auto p-6 motion-micro-scale text-center relative"
          >
            <button
              onClick={() => setCheckoutResult(null)}
              className="absolute top-3 right-3 w-11 h-11 flex items-center justify-center rounded-full text-muted hover:text-ink hover:bg-raised transition-colors"
              aria-label="Cerrar aviso de pago"
            >
              <X size={18} />
            </button>

            {checkoutResult === 'success' ? (
              <>
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-soft mb-4 border border-success shadow-sm">
                  <CheckCircle2 className="h-7 w-7 text-success" />
                </div>
                <h3 className="text-lg font-extrabold text-ink mb-2 tracking-tight">¡Suscripción Activada!</h3>
                <p className="text-sm text-muted mb-6 leading-relaxed">
                  Tu plan se ha actualizado correctamente. Ya puedes disfrutar de todas las ventajas.
                </p>
              </>
            ) : (
              <>
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-info-soft mb-4 border border-info shadow-sm">
                  <XCircle className="h-7 w-7 text-info" />
                </div>
                <h3 className="text-lg font-extrabold text-ink mb-2 tracking-tight">Pago No Completado</h3>
                <p className="text-sm text-muted mb-6 leading-relaxed">
                  Has cancelado el proceso de pago. Puedes intentarlo de nuevo cuando quieras desde tu perfil.
                </p>
              </>
            )}

            <button
              onClick={() => setCheckoutResult(null)}
              className="w-full min-h-11 py-3 bg-action text-white font-bold rounded-xl hover:bg-action-hover transition-all shadow-lg shadow-black transform active:scale-[0.98]"
            >
              {checkoutResult === 'success' ? 'Continuar' : 'Entendido'}
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col relative w-full h-full min-w-0 min-h-0">
        <ChatHeader
          onOpenSidebar={() => setSidebarOpen(true)}
          onReportClick={() => {
            setSidebarOpen(false); // Cerramos barra si está abierta
            setReportModalOpen(true);
          }}
          serviceStatus={serviceStatus}
          onOpenTemplates={() => openPanel('templates')}
        />
        <div
          ref={messagesScrollRef}
          onScroll={handleMessagesScroll}
          onWheel={handleManualScrollIntent}
          onPointerDown={handleManualScrollIntent}
          onTouchStart={handleManualScrollIntent}
          onTouchMove={handleManualScrollIntent}
          className="chat-timeline flex-1 min-h-0 overflow-y-auto pb-2 px-2 md:px-4 w-full no-scrollbar flex flex-col"
        >
          {!isOnline && (
            <ChatStatusNotice
              tone="offline"
              title="Sin conexión"
              detail="Juris conservará la pantalla estable y recuperará datos cuando vuelva Internet."
            />
          )}
          {isNewChat ? (
            <EmptyChatActions onSend={(text) => { if (!isPreviewSendingDisabled) void handleSendMessage(text, [], null); }} disabled={isInputLoading || isSavingReasoning || isPreviewSendingDisabled} />
          ) : (
            <div className="w-full flex flex-col min-h-0">
              {currentChatId && messagesLoadState === 'loading' && visibleMessages.length === 0 && !isPreparingSendInCurrentChat && (
                <ChatStatusNotice
                  tone="loading"
                  title="Cargando chat"
                  detail="Estoy recuperando los mensajes de este chat."
                />
              )}

              {currentChatId && messagesLoadState === 'error' && visibleMessages.length === 0 && (
                <ChatStatusNotice
                  tone={isOnline ? 'error' : 'offline'}
                  title={isOnline ? 'No he podido cargar este chat' : 'Sin conexión'}
                  detail={isOnline ? 'Prueba de nuevo en unos segundos.' : 'El historial aparecerá al recuperar la conexión.'}
                />
              )}

              {visibleMessages.map((msg, index) => {
                const currentDateKey = getLocalDateKey(msg.timestamp);
                const previousDateKey = index > 0 ? getLocalDateKey(visibleMessages[index - 1].timestamp) : null;
                const showDateSeparator = Boolean(currentDateKey && currentDateKey !== previousDateKey);

                return (
                  <React.Fragment key={msg.id}>
                    {showDateSeparator && <ChatDateSeparator timestamp={msg.timestamp} isFirst={index === 0} />}
                    <div
                      ref={(node) => setMessageNodeRef(msg.id, node)}
                      className="scroll-mt-24 md:scroll-mt-28"
                    >
                      <MessageBubble
                        message={msg}
                        mentionableChats={mentionableChats}
                        onOpenChat={handleSelectChat}
                        turnStartedAtMs={msg.sourceClientMessageId
                          ? userMessageStartTimes.get(msg.sourceClientMessageId) : undefined}
                        turnRun={msg.sourceClientMessageId ? userTurnRuns.get(msg.sourceClientMessageId) : undefined}
                        onReportClick={() => setReportModalOpen(true)}
                        onUpgradeClick={() => setShowPlansModal(true)}
                        shouldAnimateAppearance={animatingBotMessageId === msg.id}
                        onStreamComplete={() => completeBotAnimation(msg.id)}
                        onStreamProgress={followStreamingMessage}
                        onRetryClick={
                          canRetryMessage(msg)
                            ? () => handleRetryMessage(msg)
                            : undefined
                        }
                        isRetrying={retryingMessageIds.has(msg.id)}
                        onArtifactDownload={currentChatId ? (artifactId, fileName) => downloadDiligenceArtifact(currentChatId, artifactId, fileName) : undefined}
                        onImageDownload={currentChatId ? (index) => downloadChatImage(currentChatId, msg.id, index) : undefined}
                        onDocumentDownload={currentChatId && msg.attachmentIds?.length ? () => downloadChatDocument(currentChatId, msg.id) : undefined}
                      />
                    </div>
                  </React.Fragment>
                );
              })}

              {currentTransientError && (
                <>
                  {(() => {
                    const currentDateKey = getLocalDateKey(currentTransientError.timestamp);
                    const previousDateKey = visibleMessages.length > 0
                      ? getLocalDateKey(visibleMessages[visibleMessages.length - 1].timestamp)
                      : null;
                    return currentDateKey && currentDateKey !== previousDateKey
                      ? <ChatDateSeparator timestamp={currentTransientError.timestamp} isFirst={visibleMessages.length === 0} />
                      : null;
                  })()}
                  <MessageBubble
                    message={currentTransientError}
                    mentionableChats={mentionableChats}
                    onOpenChat={handleSelectChat}
                    onReportClick={() => setReportModalOpen(true)}
                    onUpgradeClick={() => setShowPlansModal(true)}
                    onArtifactDownload={currentChatId ? (artifactId, fileName) => downloadDiligenceArtifact(currentChatId, artifactId, fileName) : undefined}
                    onImageDownload={currentChatId ? (index) => downloadChatImage(currentChatId, currentTransientError.id, index) : undefined}
                    onDocumentDownload={currentChatId && currentTransientError.attachmentIds?.length ? () => downloadChatDocument(currentChatId, currentTransientError.id) : undefined}
                  />
                </>
              )}

              {hasActiveTypingIndicator && typingStartTime && (
                <TypingIndicator startTime={typingStartTime} isOffline={!isOnline}
                  run={[...visibleMessages].reverse().find((message) => message.sender === 'user' && message.generationStatus === 'pending')?.documentRun} />
              )}
              <div ref={messagesEndRef} className={hasActiveTypingIndicator ? 'h-2 md:h-3 shrink-0' : 'h-0'} />
            </div>
          )}
        </div>
        <div className="w-full shrink-0 z-40 bg-canvas">
          <UsagePreviewChat onManage={() => setShowPlansModal(true)} />
          <EconomicBillingChat onManage={() => setShowPlansModal(true)} executionActive={isInputLoading} />
          {reasoningError && <p className="px-5 py-2 text-sm text-warning" role="status">{reasoningError}</p>}
          <MessageInput
            key={composerDraftBinding?.key}
            draftBinding={composerDraftBinding}
            draftRevision={composerDraftRevision}
            deferClearUntilAccepted
            mentionableChats={mentionableChats}
            currentChatId={currentChatId}
            onOpenChat={handleSelectChat}
            onSendMessage={(...args) => { if (composerDraftBinding?.isActive()) void handleSendMessage(...args); }}
            isLoading={isInputLoading}
            isSendingDisabled={isSavingReasoning || isPreviewSendingDisabled}
            onStopGeneration={handleStopGeneration}
            autoFocus={isNewChat}
            reasoningPreference={getAuthorizedReasoningPreference(reasoningPreference, userTier)}
            userTier={userTier}
            onReasoningChange={handleReasoningChange}
          />
        </div>
      </div>
    </div>
    </UsagePreviewContext.Provider>
    </EconomicBillingContext.Provider>
  );
};

export default App;
